import { TypographyNumber } from "@/components/elements/numbers";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useSortable } from "@dnd-kit/sortable";
import {
  CirclePause,
  Copy,
  CopyCheck,
  MessageSquareMore,
  PhoneOff,
  Undo2,
  UserRound,
} from "lucide-react";
import { useState } from "react";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import OpenPatient from "@/components/elements/open-patient-list";
import {
  useHoldParticipantMutation,
  useKickMutation,
} from "@/graphql/generated";
import { Badge } from "@/components/ui/badge";
import { PatientInLine } from "@/types/public";
import { Role } from "@/types/role.enum";
import ConfirmKick from "./confirm-kick";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toaster } from "@/utils/toaster";
import {
  selectCurrentRoom,
  setBioLinkForPatient,
  setCopied,
} from "@/context/features/room/roomSlice";
import { useDispatch, useSelector } from "react-redux";
import { cn } from "@/lib/utils";
import { Spinner } from "@/components/ui/spinner";
import AdmitPatient from "./admit-patient";

interface Props {
  patient: PatientInLine;
  locked?: boolean;
  selectPatient: (data: {
    pid: string;
    name: string;
    visitTime: string;
    isPending: boolean;
  }) => void;
  action_call_back?: () => void;
  isWaiting: boolean;
}
const origin = import.meta.env.VITE_BIOARC_SERVICE || "https://bioarc.ir";
export default function SortablePatient({
  patient,
  selectPatient,
  action_call_back,
  locked,
  isWaiting,
}: Props) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: patient.id, disabled: locked });
  const { copied, focusedVidExist, focusedVid } =
    useSelector(selectCurrentRoom);
  const dispatch = useDispatch();

  const [confirmKickOpen, setConfirmKickOpen] = useState<boolean>(false);
  const [isGettingPatientLink, setIsGettingPatientLink] =
    useState<boolean>(false);

  const { t } = useTranslation();
  // const [removeUpcomingUser] = useRemoveUpcomingUserMutation();
  const [kick] = useKickMutation();
  const [holdParticipant] = useHoldParticipantMutation();
  const style = {
    transform: CSS.Transform.toString(transform),
    cursor: locked ? "" : "",
    transition,
  };

  const handleClickOnContact = () => {
    selectPatient({
      name: patient.fullName,
      pid: patient.id,
      visitTime: patient.visitTime,
      isPending: isWaiting,
    });
  };
  const handleClickOnCopyyLink = async () => {
    if (!patient?.id || !patient.visitTime) return;

    if (patient.bioLink) {
      await navigator.clipboard.writeText(patient.bioLink);
      dispatch(setCopied(patient.id));
      return;
    }
    setIsGettingPatientLink(true);
    try {
      const res = await fetch(
        `${origin}/VisitOnline/GenerateLink?vid=${patient.id}`
      );
      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }
      const data = await res.json();
      if (data.link) {
        dispatch(
          setBioLinkForPatient({ userId: patient.id, bioLink: data.link })
        );
        await navigator.clipboard.writeText(data.link);
        dispatch(setCopied(patient.id));
      }
    } catch (error) {
      toaster("error", t("not_copied"));
      console.error("Fetch error:", error);
    } finally {
      setIsGettingPatientLink(false);
    }
  };
  const confirmKickParticipant = () => {
    if (patient.role === Role.GUEST) handleKickParticipant();
    else setConfirmKickOpen(true);
  };
  const handleKickParticipant = () => {
    kick({
      variables: {
        identity: patient.id,
      },
    })
      .catch((err) => console.error("askToJion error:", err))
      .finally(() => {
        if (typeof action_call_back === "function") action_call_back();
      });
  };
  const handleHoldParticipant = () => {
    holdParticipant({
      variables: {
        identity: patient.id,
      },
    })
      .catch((err) => console.error("askToJion error:", err))
      .finally(() => {
        if (typeof action_call_back === "function") action_call_back();
      });
  };
  const handleReApproveParticipant = () => {
    holdParticipant({
      variables: {
        identity: patient.id,
        isTakeBack: true,
      },
    })
      .then(() => {
        toaster("info", t("added_to_queue", { name: patient.fullName }) + ".");
      })
      .catch((err) => console.error("askToJion error:", err))
      .finally(() => {
        if (typeof action_call_back === "function") action_call_back();
      });
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={cn(
        "flex gap-4 bg-background border border-input rounded-md  mb-1 p-2 md:p-2",
        patient.wantsToJoin && !patient.approved
          ? [
              "relative border-blue-400 shadow-sm shadow-blue-400/40",
              "before:content-[''] before:absolute before:inset-0 before:rounded-[inherit] before:pointer-events-none",
              "before:shadow-[0_0_5px_4px_rgba(59,130,246,0.35)]",
              "before:animate-[pulse_1.8s_ease-in-out_infinite]",
            ].join(" ")
          : ""
      )}
    >
      <div className="relative">
        {focusedVid === patient.id && (
          <div
            className={cn(
              "w-2 h-2 rounded-full absolute top-1 right-0.5 z-20",
              focusedVidExist ? "bg-green-400" : "bg-red-500"
            )}
          ></div>
        )}

        <Avatar
          {...(locked ? {} : listeners)}
          className={cn(
            "w-12 h-12 border hover:shadow-2xl hover:border-primary",
            isWaiting ? "" : "cursor-grab active:cursor-grabbing"
          )}
        >
          <AvatarImage src={patient?.avatarUrl || ""} />
          <AvatarFallback>
            <UserRound strokeWidth={1.5} className="text-gray-600" />
          </AvatarFallback>
        </Avatar>
      </div>
      <div className="grow flex flex-col gap-1 text-foreground">
        <span>
          {patient.fullName}
          {patient.role.toUpperCase() !== Role.USER && (
            <Badge className="mx-1" variant={"outline"}>
              {t(patient.role)}
            </Badge>
          )}
        </span>
        <div className="flex gap-3 flex-wrap">
          <TypographyNumber value={patient.visitTime} />
          {patient.wantsToJoin && !patient.approved && (
            <Badge variant={"default"}>{t("wait_submit")}</Badge>
          )}
          {patient.isDone && (
            <Badge className="" variant={"outlineDanger"}>
              {t("done")}
            </Badge>
          )}
          {patient.isPaused && (
            <Badge variant={"outlineAlert"}>{t("hold")}</Badge>
          )}
        </div>
      </div>
      {isWaiting ? (
        <div className="flex gap-1 items-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={"outline"}
                className="w-8 h-8 p-1"
                onClick={handleClickOnContact}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <MessageSquareMore size={20} strokeWidth={1.5} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">{t("contact")}</TooltipContent>
          </Tooltip>
          <AdmitPatient hash={patient.hash} />
        </div>
      ) : (
        <div className="flex gap-1 items-center">
          {!patient.approved && !patient.isDone && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={"outline"}
                  className="w-8 h-8 p-1"
                  onClick={handleClickOnContact}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <MessageSquareMore size={20} strokeWidth={1.5} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">{t("contact")}</TooltipContent>
            </Tooltip>
          )}
          {!patient.approved && !patient.wantsToJoin && !patient.isDone && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={"outline"}
                  className="w-8 h-8 p-1"
                  onClick={handleClickOnCopyyLink}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  {isGettingPatientLink ? (
                    <Spinner />
                  ) : copied === patient.id ? (
                    <CopyCheck size={20} strokeWidth={1.5} />
                  ) : (
                    <Copy size={20} strokeWidth={1.5} />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                {copied === patient.id ? t("copied") : t("copy")}
              </TooltipContent>
            </Tooltip>
          )}
          {patient.approved && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={"outline"}
                    className="w-8 h-8 p-1 border-orange-400"
                    onClick={handleHoldParticipant}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <CirclePause
                      className="text-orange-400"
                      size={20}
                      strokeWidth={1.5}
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">{t("put_hold")}</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={"outline"}
                    className="w-8 h-8 p-1 border-destructive"
                    onClick={confirmKickParticipant}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <PhoneOff
                      className="text-destructive"
                      size={20}
                      strokeWidth={1.5}
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">{t("end_session")}</TooltipContent>
              </Tooltip>

              <ConfirmKick
                open={confirmKickOpen}
                onOpenChange={setConfirmKickOpen}
                confirmCallback={(data) => {
                  kick({
                    variables: {
                      identity: patient.id,
                      info: data,
                    },
                  })
                    .catch((err) => console.error("askToJion error:", err))
                    .finally(() => {
                      if (typeof action_call_back === "function")
                        action_call_back();
                    });
                  // handleKickParticipant()
                }}
              />
            </>
          )}
          {patient.isDone && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={"outline"}
                  className="w-8 h-8 p-1"
                  onClick={handleReApproveParticipant}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <Undo2 className="text-primary" size={20} strokeWidth={1.5} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{t("take_back")}</p>
              </TooltipContent>
            </Tooltip>
          )}
          {patient.wantsToJoin && !patient.approved && (
            <OpenPatient
              userId={patient.id}
              isToast={false}
              onApproved={action_call_back}
            />
          )}
        </div>
      )}
    </div>
  );
}
